import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Sidebar } from 'primereact/sidebar';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Chip } from 'primereact/chip';
import './InAppAgentWidget.css';

const InAppAgentWidget = ({ visible: controlledVisible, onToggle }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([
        {
            id: 1,
            sender: 'agent',
            text: "👋 **Hello! I'm your AM In-App Financial Agent (`aminappagent`).**\n\nI can analyze cash flow, audit budget estimates against actuals, classify transactions with AI, and detect spending anomalies across your accounts.",
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            tools: []
        }
    ]);
    const [inputQuery, setInputQuery] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [backendOnline, setBackendOnline] = useState(true);
    const messagesEndRef = useRef(null);

    // Sync if parent controls visibility
    useEffect(() => {
        if (controlledVisible !== undefined) {
            setIsOpen(controlledVisible);
        }
    }, [controlledVisible]);

    const handleToggle = (openState) => {
        setIsOpen(openState);
        if (onToggle) onToggle(openState);
    };

    // Auto scroll to bottom
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        if (isOpen) {
            scrollToBottom();
        }
    }, [messages, isOpen]);

    // Check health on mount
    useEffect(() => {
        axios.get('/v1/inappagent/health')
            .then(res => {
                if (res.data && res.data.status === 'ONLINE') {
                    setBackendOnline(true);
                }
            })
            .catch(() => setBackendOnline(false));
    }, []);

    const sendMessage = async (textToSend) => {
        const query = (textToSend || inputQuery).trim();
        if (!query || isLoading) return;

        const userMsg = {
            id: Date.now(),
            sender: 'user',
            text: query,
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };

        setMessages(prev => [...prev, userMsg]);
        setInputQuery('');
        setIsLoading(true);

        try {
            const token = localStorage.getItem('bearerToken');
            const headers = token ? { Authorization: `Bearer ${token}` } : {};

            const res = await axios.post('/v1/inappagent/chat', { message: query }, { headers });
            const data = res.data;

            const agentMsg = {
                id: Date.now() + 1,
                sender: 'agent',
                text: data.reply || "Processed successfully.",
                tools: data.tools_used || [],
                data: data.data,
                timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            };
            setMessages(prev => [...prev, agentMsg]);
        } catch (err) {
            const errorMsg = {
                id: Date.now() + 1,
                sender: 'agent',
                text: `⚠️ **Connection Error**: Unable to reach backend agent: ${err.response?.data?.error || err.message}`,
                tools: [],
                timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            };
            setMessages(prev => [...prev, errorMsg]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            sendMessage();
        }
    };

    const clearChat = () => {
        setMessages([
            {
                id: Date.now(),
                sender: 'agent',
                text: "🧹 Chat cleared! How can I assist with your financial ledger today?",
                timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                tools: []
            }
        ]);
    };

    const quickPrompts = [
        { label: "📊 2024 Cash Flow", query: "What was our total cash flow in 2024?" },
        { label: "🏷️ Categorize Chevron", query: "Categorize Chevron Gas Station $35.00" },
        { label: "🎯 Budget vs Actuals", query: "Compare budget vs actuals for May 2024" },
        { label: "⚠️ Detect Anomalies", query: "Detect spending anomalies and duplicate charges" },
        { label: "📋 Financial Metadata", query: "List all accounts and active categories" }
    ];

    // Helper to render basic markdown formatting cleanly
    const renderMarkdownContent = (content) => {
        if (!content) return null;
        const lines = content.split('\n');
        return lines.map((line, idx) => {
            // Table row detection
            if (line.startsWith('|') && line.endsWith('|')) {
                const cells = line.split('|').map(c => c.trim()).filter((_, i, arr) => i > 0 && i < arr.length - 1);
                if (cells.every(c => c.includes('---') || c.includes(':---'))) {
                    return null; // Skip divider row
                }
                return (
                    <div key={idx} style={{ display: 'flex', gap: '8px', padding: '3px 0', borderBottom: '1px solid #e2e8f0', fontSize: '0.8rem' }}>
                        {cells.map((cell, cIdx) => (
                            <span key={cIdx} style={{ flex: 1, fontWeight: idx === 0 ? '600' : 'normal' }}>
                                {cell.replace(/\*\*/g, '')}
                            </span>
                        ))}
                    </div>
                );
            }

            // Bullet points
            if (line.trim().startsWith('- ') || line.trim().startsWith('* ')) {
                const clean = line.trim().substring(2);
                return (
                    <div key={idx} style={{ paddingLeft: '8px', margin: '3px 0', display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                        <span style={{ color: '#3b82f6', fontSize: '0.7rem' }}>●</span>
                        <span>{formatInlineMarkdown(clean)}</span>
                    </div>
                );
            }

            // Regular line
            return (
                <div key={idx} style={{ margin: line.trim() ? '4px 0' : '8px 0' }}>
                    {formatInlineMarkdown(line)}
                </div>
            );
        });
    };

    const formatInlineMarkdown = (text) => {
        // Replace bold **text** and backticks `code`
        const parts = text.split(/(\*\*.*?\*\*|`.*?`)/g);
        return parts.map((part, i) => {
            if (part.startsWith('**') && part.endsWith('**')) {
                return <strong key={i} style={{ color: '#0f172a' }}>{part.slice(2, -2)}</strong>;
            }
            if (part.startsWith('`') && part.endsWith('`')) {
                return <code key={i} style={{ backgroundColor: '#f1f5f9', color: '#1e40af', padding: '1px 5px', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.85em' }}>{part.slice(1, -1)}</code>;
            }
            return part;
        });
    };

    return (
        <>
            {/* Floating Action Button (Presented as In-App Agent Icon) */}
            <button
                className="am-agent-fab"
                onClick={() => handleToggle(true)}
                title="Ask AM Financial Agent (aminappagent)"
                aria-label="Open In-App Agent"
            >
                <i className="pi pi-sparkles"></i>
                <div className={`am-agent-status-pulse ${backendOnline ? '' : 'am-status-offline'}`} />
            </button>

            {/* In-App Agent Conversation Drawer */}
            <Sidebar
                visible={isOpen}
                position="right"
                onHide={() => handleToggle(false)}
                className="am-agent-sidebar"
                style={{ width: '450px', maxWidth: '100vw' }}
                showCloseIcon={false}
            >
                {/* Header */}
                <div className="am-agent-header">
                    <div className="am-agent-title-row">
                        <div className="am-agent-avatar">
                            <i className="pi pi-sparkles"></i>
                        </div>
                        <div>
                            <h3 className="am-agent-title">AM Financial Assistant</h3>
                            <div className="am-agent-subtitle">
                                <span className="am-status-dot-inline"></span>
                                <span>aminappagent · Gemini 3.7 Flash · ambackend GET</span>
                            </div>
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: '6px' }}>
                        <Button
                            icon="pi pi-trash"
                            rounded
                            text
                            severity="secondary"
                            tooltip="Clear Chat"
                            onClick={clearChat}
                            style={{ color: '#cbd5e1' }}
                        />
                        <Button
                            icon="pi pi-times"
                            rounded
                            text
                            severity="secondary"
                            onClick={() => handleToggle(false)}
                            style={{ color: '#cbd5e1' }}
                        />
                    </div>
                </div>

                {/* Quick Action Prompt Chips */}
                <div className="am-agent-chips-bar">
                    {quickPrompts.map((p, idx) => (
                        <Chip
                            key={idx}
                            label={p.label}
                            className="am-agent-chip"
                            onClick={() => sendMessage(p.query)}
                        />
                    ))}
                </div>

                {/* Message Thread */}
                <div className="am-agent-messages">
                    {messages.map(msg => (
                        <div key={msg.id} className={`am-msg-row ${msg.sender === 'user' ? 'am-msg-user' : 'am-msg-agent'}`}>
                            {msg.tools && msg.tools.length > 0 && (
                                <div className="am-tool-pill">
                                    <i className="pi pi-bolt" style={{ fontSize: '0.65rem' }}></i>
                                    <span>Executed: {msg.tools.join(', ')}</span>
                                </div>
                            )}
                            <div className="am-bubble">
                                {renderMarkdownContent(msg.text)}
                            </div>
                            <span className="am-msg-time">{msg.timestamp}</span>
                        </div>
                    ))}

                    {isLoading && (
                        <div className="am-msg-row am-msg-agent">
                            <div className="am-bubble" style={{ backgroundColor: '#ffffff' }}>
                                <div className="am-typing-indicator">
                                    <div className="am-typing-dot"></div>
                                    <div className="am-typing-dot"></div>
                                    <div className="am-typing-dot"></div>
                                    <span style={{ fontSize: '0.75rem', color: '#64748b', marginLeft: '6px' }}>Analyzing ledger...</span>
                                </div>
                            </div>
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </div>

                {/* Input Bar */}
                <div className="am-agent-input-bar">
                    <InputText
                        value={inputQuery}
                        onChange={(e) => setInputQuery(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Ask anything about cash flow, budgets, expenses..."
                        disabled={isLoading}
                    />
                    <Button
                        icon="pi pi-send"
                        className="am-agent-send-btn"
                        onClick={() => sendMessage()}
                        disabled={!inputQuery.trim() || isLoading}
                    />
                </div>
            </Sidebar>
        </>
    );
};

export default InAppAgentWidget;
